import {
  AcceptedCurrencyCode,
  useCreateDonationLinkMutation,
  useEditDonationLinkMutation,
} from '../../../types/__generated-graphQL__';
import { AllFormData, amountKeys } from '../../../types/givingCheckoutForm';
import React, { FunctionComponent, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Alert from '@justgiving/alert';
import { AuthContext } from '@justgiving/auth-react';
import CheckoutPreview from '../../components/CheckoutPreview/CheckoutPreview';
import { ErrorMessage } from '@hookform/error-message';
import Loader from '@justgiving/loading';
import { PAGES } from '../../constants/pages';
import Steps from '../../components/Steps/Steps';
import classNames from 'classnames';
import { createGraphQlClient } from '../../graphQl/baseApi';
import { suggestedAmountDefaults } from '../../components/FormContext/FormContext';
import { useCharityData } from '../../context/charityContext';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useFormContext } from 'react-hook-form';
import { useHeadingSetFocusRef } from '../../hooks/useHeadingSetFocusRef';
import { useQueryClient } from '@tanstack/react-query';
import { useQueryParameters } from '../../hooks/useQueryParameters';
import { useTranslation } from 'react-i18next';

const PostDonationDetails: FunctionComponent = () => {
  const { t } = useTranslation();
  const query = useQueryParameters();
  const linkId = query.get('id');
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { charity } = useCharityData();

  const formMethods = useFormContext<AllFormData>();

  const createDonationLink = useCreateDonationLinkMutation(
    createGraphQlClient(auth),
  );

  const editDonationLink = useEditDonationLinkMutation(
    createGraphQlClient(auth),
  );

  const {
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { errors },
  } = formMethods;
  const headingRef = useHeadingSetFocusRef();

  const linkIdFromContext = watch('linkId');

  const constructSuggestedAmount = (data: AllFormData) => {
    const suggestedAmountsArray: Array<{
      amount: string;
      description?: string | null;
    }> = [];
    let suggestedAmounts = data.suggestedAmounts;
    let numberOfCustomAmounts = data.numberOfCustomAmounts;
    let defaultAmount =
      suggestedAmounts[`amount${data.suggestedAmountDefault}` as amountKeys];

    if (
      data.customAmountsEnabled === 'no' &&
      data.customAmountDescriptionsEnabled === 'no'
    ) {
      return {
        suggestedAmounts: [],
        defaultAmount: undefined,
      };
    }

    if (
      data.customAmountsEnabled === 'no' &&
      data.customAmountDescriptionsEnabled === 'yes'
    ) {
      defaultAmount =
        suggestedAmountDefaults.suggestedAmounts[
          `amount${suggestedAmountDefaults.suggestedAmountDefault}` as amountKeys
        ];
      suggestedAmounts = suggestedAmountDefaults.suggestedAmounts;
      numberOfCustomAmounts = suggestedAmountDefaults.numberOfCustomAmounts;
    }

    for (let i = 1; i <= Number(numberOfCustomAmounts); i++) {
      if (suggestedAmounts[`amount${i}` as amountKeys]) {
        suggestedAmountsArray.push({
          amount: suggestedAmounts[`amount${i}` as amountKeys]!,
          ...(data.customAmountDescriptionsEnabled === 'yes'
            ? {
                description:
                  data.amountDescriptions?.[`amount${i}` as amountKeys],
              }
            : {}),
        });
      }
    }

    return {
      suggestedAmounts: suggestedAmountsArray,
      defaultAmount,
    };
  };

  const onSubmit = handleSubmit(async (data) => {
    const amountData = constructSuggestedAmount(data);

    const linkData = {
      name: data.linkTitle!,
      parameters: {
        reference: data.reference,
        suggestedAmounts: amountData.suggestedAmounts,
        currencyCode:
          charity?.settlementCurrencyCode ?? AcceptedCurrencyCode.Gbp,
        defaultAmount: amountData.defaultAmount,
        exitUrl: data.landingPage,
        recurring: data.donationFrequency === 'monthly',
      },
    };

    if (linkId || linkIdFromContext) {
      editDonationLink.mutate(
        {
          input: {
            donationLinkId: linkId || linkIdFromContext!,
            ...linkData,
          },
        },
        {
          onSuccess: async (data) => {
            // TODO Invalidate the cached version of this link as well
            await queryClient.invalidateQueries({
              queryKey: ['donationLinks.infinite'],
              exact: false,
            });
            await queryClient.invalidateQueries({
              queryKey: ['donationLinks'],
              exact: false,
            });
            navigate(
              `${PAGES.LinkDetails}?id=${
                data.editDonationLink!.donationLinkId
              }`,
            );
          },
        },
      );
    } else {
      createDonationLink.mutate(
        {
          input: linkData,
        },
        {
          onSuccess: async (data) => {
            await queryClient.invalidateQueries({
              queryKey: 'donationLinks.infinite',
            });
            setValue('linkId', data.createDonationLink!.donationLinkId);
            navigate(
              `${PAGES.LinkDetails}?id=${
                data.createDonationLink!.donationLinkId
              }`,
            );
          },
        },
      );
    }
  });

  useDocumentTitle(t('postDonationDetails.heading'));

  return (
    <>
      {(editDonationLink.isError || createDonationLink.isError) && (
        <Alert type="error">{t('error.mutationError')}</Alert>
      )}

      <div className="giving-checkout-page">
        <div className="giving-checkout-page-form">
          <form noValidate method="post" onSubmit={onSubmit}>
            <Steps start={4} end={5} />
            <h1 className="jg-h3" ref={headingRef} tabIndex={-1}>
              {t('postDonationDetails.heading')}
            </h1>
            <p className="jg-space-mblg jg-color--empress jg-font-weight-regular">
              {t('postDonationDetails.description')}
            </p>

            <div className="jg-form-group">
              <label
                htmlFor="linkTitle"
                className="jg-form-label jg-space-mbxs jg-color--nero"
              >
                {t('advanced.linkTitleLabel')}
              </label>
              <input
                type="text"
                className={classNames('jg-form-control', {
                  'jg-form-control--error': Boolean(errors.linkTitle),
                })}
                id="linkTitle"
                placeholder={t('advanced.linkTitlePlaceholder')}
                maxLength={50}
                aria-invalid={Boolean(errors.linkTitle)}
                {...register('linkTitle', {
                  required: {
                    value: true,
                    message: t('advanced.linkTitleError.required'),
                  },
                  minLength: {
                    value: 2,
                    message: t('advanced.linkTitleError.minLength'),
                  },
                })}
              />
              {Boolean(errors.linkTitle) ? (
                <ErrorMessage
                  errors={errors}
                  name="linkTitle"
                  render={({ message }) => {
                    return (
                      <p className="jg-form-error-copy" role="alert">
                        {message}
                      </p>
                    );
                  }}
                />
              ) : (
                <p className="jg-font-weight-regular jg-font-size--small jg-color--empress jg-space-ptxs">
                  {t('advanced.linkTitleCopy')}
                </p>
              )}
            </div>
            <div className="jg-form-group">
              <label
                htmlFor="landingPage"
                className="jg-form-label jg-space-mbxs jg-color--nero"
              >
                {t('advanced.landingPageLabel')}
              </label>
              <input
                type="text"
                className={classNames('jg-form-control', {
                  'jg-form-control--error': Boolean(errors.landingPage),
                })}
                id="landingPage"
                placeholder="e.g. http://www.yourcharity.com/thankyou"
                aria-invalid={Boolean(errors.landingPage)}
                {...register('landingPage', {
                  pattern: {
                    value:
                      // eslint-disable-next-line no-useless-escape
                      /^(?:http(?:s?)\:\/\/|~\/|\/)?(?:\w+:\w+@@)?((?:(?:[-\w\d{1-3}]+\.)+(?:[a-z]{2,24}))|((\b25[0-5]\b|\b[2][0-4][0-9]\b|\b[0-1]?[0-9]?[0-9]\b)(\.(\b25[0-5]\b|\b[2][0-4][0-9]\b|\b[0-1]?[0-9]?[0-9]\b)){3}))(?::[\d]{1,5})?(?:(?:(?:\/(?:[-\w~!$+|.,=]|%[a-f\d]{2})+)+|\/)+|\?|#)?(?:(?:\?(?:[-\w~!$+|.,*:]|%[a-f\d{2}])+=?(?:[-\w~!$+|.,*:=]|%[a-f\d]{2})*)(?:&(?:[-\w~!$+|.,*:]|%[a-f\d{2}])+=?(?:[-\w~!$+|.,*:=]|%[a-f\d]{2})*)*)*(?:#(?:[-\w~!$ |\/.,*:;=]|%[a-f\d]{2})*)?$/,
                    message: t('advanced.landingPageError.pattern'),
                  },
                })}
              />
              {Boolean(errors.landingPage) ? (
                <ErrorMessage
                  errors={errors}
                  name="landingPage"
                  render={({ message }) => {
                    return (
                      <p className="jg-form-error-copy" role="alert">
                        {message}
                      </p>
                    );
                  }}
                />
              ) : (
                <p className="jg-font-weight-regular jg-font-size--small jg-color--empress jg-space-ptxs">
                  {t('advanced.landingPageCopy')}
                </p>
              )}
            </div>
            <div className="jg-form-group">
              <label
                htmlFor="donationReference"
                className="jg-form-label jg-space-mbxs jg-color--nero"
              >
                {t('advanced.referenceLabel')}
              </label>
              <input
                type="text"
                className={classNames('jg-form-control', {
                  'jg-form-control--error': Boolean(errors.reference),
                })}
                id="donationReference"
                placeholder="e.g. 12345"
                aria-invalid={Boolean(errors.reference)}
                {...register('reference', {
                  maxLength: {
                    value: 50,
                    message: t('advanced.referenceError.maxLength'),
                  },
                  pattern: {
                    value:
                      /^[^\u2700-\u27BF\uE000-\uF8FF\uD83C\uDC00-\uDFFF\uD83D\uDC00-\uDFFF\u2011-\u26FF\uD83E\uDD10-\uDDFF]*$/,
                    message: t('advanced.referenceError.patternEmoji'),
                  },
                })}
              />
              {Boolean(errors.reference) ? (
                <ErrorMessage
                  errors={errors}
                  name="reference"
                  render={({ message }) => {
                    return (
                      <p className="jg-form-error-copy" role="alert">
                        {message}
                      </p>
                    );
                  }}
                />
              ) : (
                <p className="jg-font-weight-regular jg-color--empress jg-font-size--small jg-space-ptxs">
                  {t('advanced.referenceCopy')}
                </p>
              )}
            </div>

            <button
              className="jg-btn"
              type="submit"
              data-qa="postdonation-continue"
              data-track="postdonation-continue"
              disabled={
                createDonationLink.isPending || editDonationLink.isPending
              }
            >
              {createDonationLink.isPending || editDonationLink.isPending ? (
                <Loader
                  colour="white"
                  size="small"
                  accessibleLabel={t('common.loading')}
                />
              ) : (
                t('common.complete')
              )}
            </button>

            <button
              className="giving-checkout-button-link"
              type="button"
              onClick={() =>
                navigate(`${PAGES.Descriptions}${location.search}`)
              }
            >
              {t('common.back')}
            </button>
          </form>
        </div>
        <div className="giving-checkout-page-preview">
          <CheckoutPreview />
        </div>
      </div>
    </>
  );
};

export default PostDonationDetails;
